import { Button } from '@/components/ui/button';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import {
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  HeartPulse,
  History,
  LayoutDashboard,
  PlayCircle,
  UserCircle,
  Users,
} from 'lucide-react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

interface MenuItem {
  key: string;
  icon: React.ReactNode;
  label: string;
  requiresAuth?: boolean;
  requiresAdminOrMedic?: boolean;
}

export default function Sidebar({ collapsed, onCollapse }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isAdminOrMedic } = usePermissions();

  // All menu items with their permission requirements
  const allMenuItems: MenuItem[] = [
    {
      key: '/dashboard',
      icon: <LayoutDashboard className='h-5 w-5' />,
      label: 'Panou de Control',
      requiresAuth: true, // All authenticated users
    },
    {
      key: '/flow-clinical',
      icon: <PlayCircle className='h-5 w-5' />,
      label: 'Sesiune Clinică',
      requiresAuth: true, // All authenticated users
    },
    {
      key: '/clinical-sessions',
      icon: <History className='h-5 w-5' />,
      label: 'Istoric Sesiuni',
      requiresAdminOrMedic: true, // Only Admin/Medic
    },
    {
      key: '/users',
      icon: <Users className='h-5 w-5' />,
      label: 'Utilizatori',
      requiresAdminOrMedic: true, // Only Admin/Medic
    },
    {
      key: '/persons',
      icon: <UserCircle className='h-5 w-5' />,
      label: 'Persoane',
      requiresAdminOrMedic: true, // Only Admin/Medic
    },
    {
      key: '/person-health-profiles',
      icon: <HeartPulse className='h-5 w-5' />,
      label: 'Profile Sănătate',
      requiresAuth: true, // All authenticated users
    },
    {
      key: '/assessments',
      icon: <ClipboardList className='h-5 w-5' />,
      label: 'Evaluări',
      requiresAdminOrMedic: true, // Only Admin/Medic
    },
  ];

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter((item) => {
    // If requires Admin/Medic, check that permission
    if (item.requiresAdminOrMedic) {
      return isAdminOrMedic();
    }
    // If requires auth, user must be authenticated (which they are if they see the sidebar)
    if (item.requiresAuth) {
      return !!user;
    }
    // Default: show item
    return true;
  });

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-16 z-40 h-[calc(100vh-64px)] border-r border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-all duration-200',
        collapsed ? 'w-20' : 'w-60',
      )}
    >
      <div className='flex h-full flex-col'>
        {/* Menu Items */}
        <nav className='flex-1 space-y-1 p-4'>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.key;
            return (
              <button
                key={item.key}
                onClick={() => handleMenuClick(item.key)}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground',
                  collapsed && 'justify-center',
                )}
                title={collapsed ? item.label : undefined}
              >
                {item.icon}
                {!collapsed && <span>{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className='border-t border-border/40 p-4'>
          <Button
            variant='ghost'
            size='icon'
            onClick={() => onCollapse(!collapsed)}
            className='w-full'
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <ChevronRight className='h-5 w-5' /> : <ChevronLeft className='h-5 w-5' />}
            {!collapsed && <span className='ml-2 text-sm'>Colapsare</span>}
          </Button>
        </div>
      </div>
    </aside>
  );
}
